import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MicIcon from "@mui/icons-material/Mic";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import { Box, Button, Tooltip } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useState } from "react";

import type { ProjectActorVoiceListItemDto } from "Infrastructure/Api/Api";
import { VoiceLlm } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  createMediaSegment,
  createPauseSegment,
  createVoiceSegment,
  isVoiceSegment,
  useScriptEditor,
} from "Projects/Components/ScriptEditor/ScriptEditorContext";
import { AddVariableDialog } from "./AddVariableDialog";

interface ScriptEditorToolbarProps {
  actorVoices?: ProjectActorVoiceListItemDto[];
}

/**
 * Top toolbar for the script editor.
 *
 * "Add voice / pause / media" are always visible. "Add variable" and "Add
 * reaction" require an active text-edit (they only make sense at a cursor
 * position inside a voice segment). "Add reaction" is further gated to voices
 * whose model is ElevenLabs v3 — other models ignore audio tags.
 */
export const ScriptEditorToolbar = ({
  actorVoices,
}: ScriptEditorToolbarProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const {
    addSegment,
    uiDefaults,
    script,
    setScript,
    activeEditRef,
    activeEditSegmentIndex,
    splitVoiceSegment,
    wrapSelectionWithVariable,
    openReactionEditor,
    isEditable,
  } = useScriptEditor();

  const [varDialogOpen, setVarDialogOpen] = useState(false);
  const [varSelectedText, setVarSelectedText] = useState("");
  const [pendingVarState, setPendingVarState] = useState<{
    text: string;
    selStart: number;
    selEnd: number;
    segmentIndex: number;
  } | null>(null);

  const preventBlur = (e: React.MouseEvent) => e.preventDefault();

  const handleAddVoice = () => {
    const active = activeEditRef.current;
    if (active) {
      const { text, selStart, selEnd } = active.getTextAndSelection();
      splitVoiceSegment(active.segmentIndex, text, selStart, selEnd, "voice");
      active.cancelEdit();
      return;
    }
    addSegment(
      createVoiceSegment({
        actorVoiceID: uiDefaults.defaultActorVoiceID ?? "",
        languageOverride: uiDefaults.defaultLanguage,
        speedFactor: uiDefaults.defaultSpeedFactor,
        volumePercent: uiDefaults.defaultVolumePercent,
      }),
    );
  };

  const handleAddPause = () => {
    const active = activeEditRef.current;
    if (active) {
      const { text, selStart, selEnd } = active.getTextAndSelection();
      splitVoiceSegment(
        active.segmentIndex,
        text,
        selStart,
        selStart === selEnd ? selStart : selEnd,
        "pause",
      );
      active.cancelEdit();
      return;
    }
    addSegment(createPauseSegment());
  };

  const handleAddMedia = () => {
    const active = activeEditRef.current;
    if (active) {
      const { text, selStart, selEnd } = active.getTextAndSelection();
      splitVoiceSegment(
        active.segmentIndex,
        text,
        selStart,
        selStart === selEnd ? selStart : selEnd,
        "media",
      );
      active.cancelEdit();
      return;
    }
    addSegment(createMediaSegment());
  };

  const handleAddVariable = () => {
    const active = activeEditRef.current;
    if (!active) return;
    const { text, selStart, selEnd } = active.getTextAndSelection();
    const selectedText = text.slice(selStart, selEnd).trim();
    setPendingVarState({
      text,
      selStart,
      selEnd,
      segmentIndex: active.segmentIndex,
    });
    setVarSelectedText(selectedText);
    setVarDialogOpen(true);
  };

  const handleVariableConfirm = (name: string, defaultValue: string) => {
    if (pendingVarState) {
      wrapSelectionWithVariable(
        pendingVarState.segmentIndex,
        pendingVarState.text,
        pendingVarState.selStart,
        pendingVarState.selEnd,
        name,
      );
    } else {
      const existing = script.variables.find((v) => v.name === name);
      if (!existing) {
        setScript({
          ...script,
          variables: [...script.variables, { name, defaultValue }],
        });
      }
    }
    setPendingVarState(null);
    setVarDialogOpen(false);
  };

  // "Add reaction" visibility: a voice segment is being edited AND its voice
  // is ElevenLabs v3.
  const activeSegment =
    activeEditSegmentIndex != null
      ? script.segments[activeEditSegmentIndex]
      : null;
  const activeActorVoiceID =
    activeSegment && isVoiceSegment(activeSegment)
      ? activeSegment.actorVoiceID
      : null;
  const activeVoiceLlm = actorVoices?.find(
    (av) => av.actorVoiceID === activeActorVoiceID,
  )?.voiceLlm;
  const isReactionButtonVisible =
    activeEditSegmentIndex != null &&
    activeSegment != null &&
    isVoiceSegment(activeSegment) &&
    activeVoiceLlm === VoiceLlm.ElevenLabs_V3;
  const isVariableButtonVisible = activeEditSegmentIndex != null;

  const handleAddReaction = () => {
    const active = activeEditRef.current;
    if (!active) return;
    const cursor = active.getTextAndSelection().selStart;
    const segIdx = active.segmentIndex;
    // Atomic commit + insert so the returned index matches the post-state
    // spans array (can't rely on setState updater return values in React 18).
    const insertedIndex = active.commitAndInsertReactionAtCursor(cursor);
    // Open the editor modal for the freshly-inserted span. `isNew: true` so
    // that cancelling the modal without picking anything removes the empty
    // chip rather than leaving an orphan.
    openReactionEditor(segIdx, insertedIndex, true);
  };

  if (!isEditable) return null;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Tooltip title={t(Resources.JobDetail.AddVoiceSegment)}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<MicIcon />}
            onMouseDown={preventBlur}
            onClick={handleAddVoice}
            sx={{ fontSize: "12px", py: 0.5 }}
          >
            {t(Resources.JobDetail.AddVoiceSegment)}
          </Button>
        </Tooltip>

        <Tooltip title={t(Resources.JobDetail.AddPause)}>
          <Button
            size="small"
            variant="outlined"
            color="info"
            startIcon={<PauseCircleIcon />}
            onMouseDown={preventBlur}
            onClick={handleAddPause}
            sx={{ fontSize: "12px", py: 0.5 }}
          >
            {t(Resources.JobDetail.AddPause)}
          </Button>
        </Tooltip>

        <Tooltip title={t(Resources.JobDetail.AddBackgroundAudio)}>
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={<MusicNoteIcon />}
            onMouseDown={preventBlur}
            onClick={handleAddMedia}
            sx={{ fontSize: "12px", py: 0.5 }}
          >
            {t(Resources.JobDetail.AddBackgroundAudio)}
          </Button>
        </Tooltip>

        {isVariableButtonVisible && (
          <Tooltip title={t(Resources.JobDetail.AddVariableTooltip)}>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<TextFieldsIcon />}
              onMouseDown={preventBlur}
              onClick={handleAddVariable}
              sx={{ fontSize: "12px", py: 0.5 }}
            >
              {t(Resources.JobDetail.AddVariable)}
            </Button>
          </Tooltip>
        )}

        {isReactionButtonVisible && (
          <Tooltip title={t(Resources.JobDetail.AddReactionTooltip)}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onMouseDown={preventBlur}
              onClick={handleAddReaction}
              sx={{
                fontSize: "12px",
                py: 0.5,
                color: theme.colors.reactionAccent,
                borderColor: theme.colors.reactionAccent,
                "&:hover": {
                  borderColor: theme.colors.reactionAccent,
                  backgroundColor: alpha(theme.colors.reactionAccent, 0.08),
                },
              }}
            >
              {t(Resources.JobDetail.AddReaction)}
            </Button>
          </Tooltip>
        )}
      </Box>

      <AddVariableDialog
        open={varDialogOpen}
        selectedText={varSelectedText}
        onConfirm={handleVariableConfirm}
        onClose={() => {
          setVarDialogOpen(false);
          setPendingVarState(null);
        }}
      />
    </>
  );
};
